/*
 * PANOU UNIFICAT LINII (Faza B, audit iul 2026)
 * Un singur builder de card linie, folosit de Dashboard Producție ȘI de
 * Administrare (Overview). Datele vin dintr-un singur apel /api/lines/panel
 * (sursa unică server-side) — elimină logica duplicată client-side.
 * Acțiunile diferă după context: opts.admin=true adaugă butonul ⚙ Configurare.
 */
import { api, deschideAlarme, escHtml, state, toast } from './core';
import { askConfirm } from './dialogs';

export interface PanelLine {
    id: string; product: string | null; icon: string | null; line_type: string | null;
    status: 'running' | 'stopped' | 'unknown'; is_active: boolean;
    main_machine: string | null; main_machine_hours: number;
    mold: { id: string; cycles: number } | null;
    power: { kw_live: number | null; kw_avg_1h: number | null; kwh_today: number; kwh_partial: boolean };
    productivity: { bph_final: number | null; source: string; source_machine: string | null;
                    bph_machine: number | null; scrap_pct: number | null; nominal_cph: number | null;
                    caps_today: number | null; caps_today_partial: boolean };
    oee?: { oee: number | null; availability: number | null; performance: number | null;
            quality: number | null; produced_today: number | null;
            window_hours: number; partial: boolean } | null;
    health: { pct: number; overdue: number; next_due_hours: number | null;
              next_due_action: string | null; next_due_machine: string | null; basis: string;
              /** 1 nov 2026: textul stării din sursa unică (routes/maintenance/_stare.py) */
              next_due_text?: string | null };
    alarms: { critical: number; warning: number; last24h: number };
    /** Atenționările necitite scrise de aplicație despre utilajele liniei (10 oct 2026). */
    atentionari?: { necitite: number; critice: number };
    /** Alarme de la utilaje care revin prea des: ≥ 3 pe zi sau în creștere (11 oct 2026, services/alarme_dese.py). */
    alarme_dese?: number;
    /** Alarme prin care utilajele liniei CER mentenanță (11 oct 2026) — nu defecte; legate de lucrări în Istoric Alarme. */
    mentenanta_ceruta?: number;
    work: { active_urgent: number };
    /** Utilaje marcate „oprite pentru mentenanță" de un om, cu marcajul încă aprins. */
    mentenanta_marcata?: Array<{ machine_id: string; nume: string; din: string | null;
                                 motiv: string; autor: string | null }>;
    utilities: Array<{ id: string; status: string; kw: number | null; serves: string[] }>;
    dominant_signal: { level: string; text: string } | null;
    machines: Array<{ id: string; name: string; type: string; kw: number | null; alarms: number }>;
}

export interface PanelData {
    generated_at: string;
    lines: PanelLine[];
    // `overdue` = mentenanțe depășite pe utilitate (adăugat 8 aug 2026)
    utilities_card: { total_factory_kw: number | null;
                      entries: Array<{ id: string; group: string | null; kw: number | null;
                                       status: string; serves: string[]; overdue?: number }> };
}

export async function fetchLinesPanel(): Promise<PanelData> {
    return api('/api/lines/panel');
}

const SOURCE_LABELS: Record<string, string> = {
    sfm: 'final (SFM)', foldare: 'final (numărat la cutie)', machine: 'mașină', nominal: 'estimat', none: '',
};

function _fmtNum(n: number): string {
    return Math.round(n).toLocaleString('ro-RO');
}

/** Donut SVG mic pentru indicele de sănătate (verde/galben/roșu pe praguri).
 *  pct=null → „—" gri (utilaj fără date suficiente pentru scor). */
export function healthDonutSvg(pct: number | null, size = 46): string {
    const r = (size / 2) - 4;
    const c = 2 * Math.PI * r;
    if (pct == null) {
        return `<svg class="lp-health" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" role="img" aria-label="Fără scor de sănătate">
            <circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="var(--border,#334)" stroke-width="5"/>
            <text x="50%" y="54%" dominant-baseline="middle" text-anchor="middle"
                  style="font-size:${size * 0.3}px;font-weight:700;fill:var(--text-muted,#6b7590)">—</text>
        </svg>`;
    }
    const off = c * (1 - Math.max(0, Math.min(100, pct)) / 100);
    const color = pct >= 70 ? 'var(--green,#22c55e)' : pct >= 40 ? 'var(--yellow,#f59e0b)' : 'var(--red,#ef4444)';
    return `<svg class="lp-health" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" role="img" aria-label="Sănătate ${pct}%">
        <circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="var(--border,#334)" stroke-width="5"/>
        <circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="${color}" stroke-width="5"
                stroke-dasharray="${c.toFixed(1)}" stroke-dashoffset="${off.toFixed(1)}"
                stroke-linecap="round" transform="rotate(-90 ${size / 2} ${size / 2})"/>
        <text x="50%" y="54%" dominant-baseline="middle" text-anchor="middle"
              style="font-size:${size * 0.3}px;font-weight:700;fill:var(--text,#e7ecf5)">${pct}</text>
    </svg>`;
}

function _statusPill(l: PanelLine): string {
    if (!l.is_active) return '<span class="lp-pill lp-pill-off">INACTIVĂ</span>';
    if (l.status === 'running') return '<span class="lp-pill lp-pill-run"><span class="lp-dot"></span>PRODUCȚIE</span>';
    if (l.status === 'stopped') return '<span class="lp-pill lp-pill-stop">OPRITĂ</span>';
    return '<span class="lp-pill lp-pill-unk">FĂRĂ DATE</span>';
}

const _EQ_ICONS: Record<string, string> = {
    injection: '⚙️', compression: '⚙️', mold: '🔲', chiller: '❄️', cooler: '🌡️',
    compressor: '💨', dryer: '🔥', reservoir: '🛢️', control: '🖥️', inspection: '👁️',
    tampoprint: '🖨️', auxiliary: '🔧', robot: '🤖', printer: '🏷️',
};

export function buildLineCardHtml(l: PanelLine, opts: { admin?: boolean } = {}): string {
    const p = l.power, pr = l.productivity, h = l.health;
    const kwLive = p.kw_live != null ? p.kw_live.toFixed(1) : null;
    const kwAvg = p.kw_avg_1h != null ? p.kw_avg_1h.toFixed(1) : null;
    const powerTxt = kwLive != null
        ? `acum <b>${kwLive} kW</b>${kwAvg != null ? ` <small>· medie 1h ${kwAvg}</small>` : ''}`
        : '—';
    const srcLbl = SOURCE_LABELS[pr.source] || '';
    const prodTxt = pr.bph_final != null
        ? `${_fmtNum(pr.bph_final)} buc/h${srcLbl ? ` <span class="lp-src lp-src-${pr.source}">${srcLbl}</span>` : ''}`
        : '—';

    const sig = l.dominant_signal as ({ level: string; text: string; kind?: string } | null);
    const idsMasini = (l.machines || []).map(m => m.id).join(',');
    const signalHtml = sig
        ? `<button type="button" class="lp-signal lp-signal-${sig.level}" data-act="signal" data-kind="${escHtml(sig.kind || '')}" data-line="${escHtml(l.id)}" data-masini="${escHtml(idsMasini)}" title="${sig.kind === 'urgent' ? 'Vezi ce e deschis pe linie — doar vizualizare, cu finalizare' : 'Deschide direct — revii cu ↩'}">${sig.level === 'crit' ? '🔴' : '⚠️'} ${escHtml(sig.text)} <span class="lp-signal-go">›</span></button>`
        : '';
    // Marcajul „oprire pentru mentenanță" rămas aprins. Dan, 20 aug: „nu văd
    // unde apare M4 în mentenanță, iar linia chiar funcționează". Steagul stătea
    // în bază din 18 august, dar cardul arăta doar «PRODUCȚIE». Când linia merge,
    // contradicția e scrisă pe față și butonul de scoatere e chiar acolo.
    const marcaje = l.mentenanta_marcata || [];
    const marcajHtml = marcaje.map(mk => {
        const din = (mk.din || '').replace('T', ' ').slice(0, 16);
        const cine = mk.autor ? ` de ${escHtml(mk.autor)}` : '';
        const produce = l.status === 'running';
        return `<div class="lp-marcaj${produce ? ' lp-marcaj-contra' : ''}"
            title="${produce
                ? 'Utilajul e marcat „oprit pentru mentenanță", dar linia produce. Marcajul se scoate de om — aplicația nu-l stinge singură.'
                : 'Marcaj pus manual: alarmele calendaristice sunt ascunse, criticele apar ca sugestii „cât ești oprit".'}">
            ${produce ? '⚠' : '🔧'} <strong>${escHtml(mk.nume)}</strong> marcat „oprit pentru mentenanță"${din ? ` din ${escHtml(din)}` : ''}${cine}${produce ? ' — dar linia produce' : ''}
            ${mk.motiv ? `<span class="lp-marcaj-motiv">(${escHtml(mk.motiv)})</span>` : ''}
            <button type="button" class="lp-marcaj-btn" data-act="clear-stop" data-machine="${escHtml(mk.machine_id)}"
                    title="Scoate marcajul de pe ${escHtml(mk.nume)}">↺ Scoate marcajul</button>
        </div>`;
    }).join('');

    const nextDue = h.next_due_text
        ? `${escHtml(h.next_due_action || '')} — ${escHtml(h.next_due_text)}`
        : h.next_due_hours != null
        ? `${_fmtNum(h.next_due_hours)}h — ${escHtml(h.next_due_action || '')}`
        : null;

    const chips = l.machines.map(m => {
        const icon = _EQ_ICONS[m.type] || '🔧';
        const dot = m.alarms > 0 ? 'lp-chip-dot-warn' : (m.kw != null && m.kw > 1 ? 'lp-chip-dot-ok' : 'lp-chip-dot-off');
        const kw = m.kw != null && m.kw > 0.1 ? ` <small>${m.kw.toFixed(1)}kW</small>` : '';
        return `<button type="button" class="lp-chip" data-machine="${escHtml(m.id)}" data-mname="${escHtml(m.name)}">
            <span class="lp-chip-dot ${dot}"></span>${icon} ${escHtml(m.name)}${kw}</button>`;
    }).join('');

    const utils = l.utilities.map(u => {
        const st = u.status === 'running' ? '✓' : (u.status === 'stopped' ? '⏹' : '?');
        const cls = u.status === 'running' ? 'lp-util-ok' : (u.status === 'stopped' ? 'lp-util-stop' : 'lp-util-unk');
        return `<span class="lp-util ${cls}" title="Deservește: ${escHtml(u.serves.join(', '))}">❄ ${escHtml(u.id)} ${st}</span>`;
    }).join('');

    const scrap = pr.scrap_pct != null
        ? `<span class="lp-metric" title="Rebut live: mașină ${_fmtNum(pr.bph_machine || 0)} buc/h vs final ${_fmtNum(pr.bph_final || 0)} buc/h">♻ rebut ${pr.scrap_pct.toFixed(1)}%</span>`
        : '';
    const moldTxt = l.mold
        ? `<span class="lp-metric" title="Matrița activă">🔲 ${escHtml(l.mold.id)}${l.mold.cycles ? ` <small>${_fmtNum(l.mold.cycles)} cicluri</small>` : ''}</span>`
        : '';
    let oeeBadge = '';
    if (l.oee && l.oee.oee != null) {
        const v = l.oee.oee;
        const cls = v >= 75 ? 'lp-oee-ok' : v >= 50 ? 'lp-oee-warn' : 'lp-oee-crit';
        const a = l.oee.availability, pf = l.oee.performance, q = l.oee.quality;
        oeeBadge = `<span class="lp-metric lp-oee ${cls}" title="OEE = Disponibilitate ${a ?? '—'}% × Performanță ${pf ?? '—'}% × Calitate ${q ?? '—'}% · fereastră ${l.oee.window_hours}h${l.oee.partial ? ' (parțial azi)' : ''}">📈 OEE <b>${v.toFixed(0)}%</b></span>`;
    }

    const alarmBadge = (l.alarms.critical || l.alarms.warning)
        ? `<button type="button" class="lp-alarm-badge" data-line-alarms="${escHtml(l.id)}">🔔 ${l.alarms.critical ? `<b class="lp-crit">${l.alarms.critical}</b>` : ''}${l.alarms.warning ? ` <b class="lp-warn">${l.alarms.warning}</b>` : ''}</button>`
        : '';
    // Atenționările scrise de aplicație despre utilajele liniei (mașină mută, lucrări neanunțate mașinii, proces
    // tăcut…). Stăteau doar în panoul mare, de-a valma cu sistemul (10 oct 2026).
    const at = l.atentionari;
    const atentBadge = at && at.necitite
        ? `<button type="button" class="lc-atentionari${at.critice ? ' critic' : ''}" data-atentionari-linie="${escHtml(l.id)}" title="Atenționări necitite despre utilajele liniei — deschide lista">⚠ ${at.necitite} ${at.necitite === 1 ? 'atenționare' : 'atenționări'}</button>`
        : '';

    // Mașina cere mentenanță („Întreținere programată 180H”…) — nu e defect; Istoric Alarme spune ce e la noi (11 oct 2026).
    const mentBadge = l.mentenanta_ceruta
        ? `<button type="button" class="lc-dese lc-ment" data-ment-linie="${escHtml(l.id)}" title="Utilajele liniei cer mentenanță pe HMI — vezi ce e la noi pe aceeași cadență">🔧 ${l.mentenanta_ceruta} ${l.mentenanta_ceruta === 1 ? 'cerută de mașină' : 'cerute de mașină'}</button>`
        : '';
    // Alarme care se sting singure la mașină, dar revin prea des (11 oct 2026): lista, cu sugestii, e în Istoric Alarme.
    const deseBadge = l.alarme_dese
        ? `<button type="button" class="lc-dese" data-dese-linie="${escHtml(l.id)}" title="Alarme care revin prea des pe utilajele liniei (ultimele 7 zile) — deschide lista cu sugestii">↻ ${l.alarme_dese} ${l.alarme_dese === 1 ? 'alarmă deasă' : 'alarme dese'}</button>`
        : '';

    return `<div class="dash-line-card lp-card ${sig && sig.level === 'crit' ? 'lp-card-crit' : ''} ${!l.is_active ? 'lp-card-inactive' : ''}" data-line="${escHtml(l.id)}" data-product="${escHtml(l.product || '')}" role="button" tabindex="0" title="Detalii linie">
        <div class="lp-head">
            ${healthDonutSvg(h.pct)}
            <div class="lp-title">
                <div class="lp-id"><span class="lp-line-word">Linia</span> ${escHtml(l.id)}${l.product ? ` <span class="lp-product">— ${escHtml(l.product)}</span>` : ''}</div>
                <div class="lp-sub">${escHtml((l.line_type || '').toUpperCase())} · ${l.machines.length} utilaje${h.overdue ? ` · <span class="lp-warn">${h.overdue} mentenanțe depășite</span>` : ''}</div>
            </div>
            ${alarmBadge}${atentBadge}${deseBadge}${mentBadge}
            ${_statusPill(l)}
        </div>
        <div class="lp-metrics">
            <span class="lp-metric" title="Consum linie (cu distribuție utilaje partajate)">⚡ ${powerTxt}</span>
            <span class="lp-metric" title="${p.kwh_partial ? 'Parțial — colectarea a avut pauze azi; cifra acoperă doar intervalul cu date' : 'Energie de la miezul nopții (ora României)'}">🔋 ${p.kwh_today.toFixed(0)} kWh azi${p.kwh_partial ? ' <small title="date parțiale azi">≈ parțial</small>' : ''}</span>
            <span class="lp-metric" title="Productivitate produs final">🏭 ${prodTxt}</span>
            ${pr.caps_today != null ? `<span class="lp-metric" title="Capace bune azi (contor produs final${pr.caps_today_partial ? ', parțial — date de la repornirea colectoarelor azi' : ', de la miezul nopții'})">📦 <b>${_fmtNum(pr.caps_today)}</b> azi${pr.caps_today_partial ? ' <small>≈</small>' : ''}</span>` : ''}
            ${oeeBadge}
            ${scrap}
            <span class="lp-metric" title="Ore funcționare mașină principală">🕐 ${_fmtNum(l.main_machine_hours)} h</span>
            ${moldTxt}
        </div>
        ${signalHtml}
        ${marcajHtml}
        ${nextDue ? `<div class="lp-next-due" title="Următoarea mentenanță scadentă pe linie (${escHtml(h.next_due_machine || '')})">📅 ${nextDue}</div>` : ''}
        <div class="lp-chips">${chips}</div>
        ${utils ? `<div class="lp-utils">${utils}</div>` : ''}
        <div class="lp-actions">
            <button type="button" class="dash-btn dash-btn-tasks" data-act="tasks" data-line="${escHtml(l.id)}">🔧 Lucrările mele</button>
            <button type="button" class="dash-btn dash-btn-urmeaza" data-act="urmeaza" data-line="${escHtml(l.id)}" title="Ce mentenanțe urmează pe linie — de făcut cât stă">🔧 Ce urmează</button>
            <button type="button" class="dash-btn dash-btn-alarms" data-act="alarms" data-line="${escHtml(l.id)}">🔔 Alarme</button>
            <button type="button" class="dash-btn dash-btn-ai" data-act="ai" data-line="${escHtml(l.id)}" data-main="${escHtml(l.main_machine || '')}">Întreabă AI</button>
            <button type="button" class="dash-btn dash-btn-layout" data-act="detail" data-line="${escHtml(l.id)}" data-product="${escHtml(l.product || '')}">Detalii linie</button>
            ${opts.admin ? `<button type="button" class="dash-btn lp-btn-config" data-act="config" data-line="${escHtml(l.id)}">⚙ Configurare</button>` : ''}
        </div>
    </div>`;
}

export function buildUtilitiesCardHtml(uc: PanelData['utilities_card'], opts: { admin?: boolean } = {}): string {
    const vizibile = uc.entries.filter(e => e.id !== 'TRANSFORMATOR');
    const entries = vizibile.map(e => {
        const cls = e.status === 'running' ? 'lp-util-ok' : (e.status === 'stopped' ? 'lp-util-stop' : 'lp-util-unk');
        const kw = e.kw != null ? ` ${e.kw.toFixed(1)} kW` : '';
        // 8 aug 2026: utilitățile au mentenanță ca oricare utilaj, dar cardul
        // arăta doar kW și stare — restanțele lor nu se vedeau nicăieri în
        // tabloul de linii, doar în «Lucrările mele».
        const rest = (e as { overdue?: number }).overdue || 0;
        const badge = rest ? `<span class="lp-util-rest" title="${rest} mentenanțe depășite">⚠ ${rest}</span>` : '';
        const titlu = [e.serves.length ? 'Deservește: ' + e.serves.join(', ') : '',
                       rest ? `${rest} mentenanțe depășite` : ''].filter(Boolean).join(' · ');
        return `<button type="button" class="lp-chip ${cls}" data-machine="${escHtml(e.id)}" data-mname="${escHtml(e.id)}"
                    title="${escHtml(titlu)}">
            <span class="lp-chip-dot ${e.status === 'running' ? 'lp-chip-dot-ok' : 'lp-chip-dot-off'}"></span>❄ ${escHtml(e.id)}${kw}${badge}</button>`;
    }).join('');
    const total = uc.total_factory_kw != null ? `${uc.total_factory_kw.toFixed(1)} kW` : '—';
    const totalRest = vizibile.reduce((s, e) => s + ((e as { overdue?: number }).overdue || 0), 0);
    const semnal = totalRest
        ? `<span class="lp-metric lp-metric-warn" title="Mentenanțe depășite pe utilități">⚠ <b>${totalRest}</b> depășite</span>`
        : '';
    return `<div class="dash-line-card lp-card lp-card-utils" data-line="_utils">
        <div class="lp-head">
            <div class="lp-title">
                <div class="lp-id">⚙ Utilități &amp; Infrastructură</div>
                <div class="lp-sub">TRAFO · răcitoare externe · aer comprimat</div>
            </div>
            <span class="lp-metric" title="Puterea totală a fabricii (contor transformator)">⚡ total fabrică: <b>${total}</b></span>
            ${semnal}
        </div>
        <div class="lp-chips">${entries}</div>
    </div>`;
}

/* ── Navigare cu context + revenire (↩) ──────────────────────────
 * Orice salt din card memorează secțiunea de plecare și afișează o pilulă
 * fixă "↩ Înapoi la …". Pilula dispare la click pe ea sau la navigare
 * manuală din meniu. Același mecanism se refolosește pe panoul de utilaj (B3).
 */
const SECTION_LABELS: Record<string, string> = {
    dashboard: 'Dashboard', overview: 'Administrare', mytasks: 'Lucrările mele',
    journal: 'Intervenții', chat: 'Chat AI', alarm_history: 'Alarme',
};

function _activeSection(): string {
    const sec = document.querySelector('.content-section.active, section.active[id^="sec-"]');
    return sec ? sec.id.replace(/^sec-/, '') : 'dashboard';
}

export function lpNavToSection(section: string): void {
    const link = document.querySelector(`.nav-item[data-section="${section}"], .nav-links a[data-section="${section}"]`) as HTMLElement | null;
    if (link) link.click();
}

export function lpRemoveBackPill(): void {
    document.getElementById('lp-back-pill')?.remove();
}

export function lpActiveSection(): string {
    return _activeSection();
}

export function lpPushReturnAndGo(go: () => void): void {
    const from = _activeSection();
    (window as any)._lpReturn = from;
    go();
    setTimeout(() => lpShowBackPill(`↩ Înapoi la ${SECTION_LABELS[from] || from}`, () => lpNavToSection(from)), 500);
}

/** Pilulă de revenire cu destinație custom (refolosită de panoul de utilaj).
 *  Mutabilă prin tragere (mouse/deget) ca să nu acopere alte date — poziția
 *  aleasă se reține în localStorage pentru aparițiile următoare. Tap scurt
 *  (< 8px mișcare) = navigare înapoi; tragere = doar mutare. */
const _LP_PILL_POS_KEY = 'lpBackPillPos';

function _lpClampPos(x: number, y: number, el: HTMLElement): { x: number; y: number } {
    const m = 8;
    const w = el.offsetWidth || 180, h = el.offsetHeight || 44;
    return {
        x: Math.max(m, Math.min(window.innerWidth - w - m, x)),
        y: Math.max(m, Math.min(window.innerHeight - h - m, y)),
    };
}

export function lpShowBackPill(label: string, onBack: () => void): void {
    lpRemoveBackPill();
    const pill = document.createElement('button');
    pill.id = 'lp-back-pill';
    pill.type = 'button';
    pill.className = 'lp-back-pill';
    pill.textContent = label;
    pill.title = 'Tap = înapoi · trage ca să o muți dacă acoperă ceva';
    document.body.appendChild(pill);

    // poziția salvată (dacă există) — altfel rămâne pe default-ul din CSS
    try {
        const saved = JSON.parse(localStorage.getItem(_LP_PILL_POS_KEY) || 'null');
        if (saved && typeof saved.x === 'number' && typeof saved.y === 'number') {
            const p = _lpClampPos(saved.x, saved.y, pill);
            pill.style.left = p.x + 'px';
            pill.style.top = p.y + 'px';
            pill.style.bottom = 'auto';
            pill.style.transform = 'none';
        }
    } catch (_e) { /* poziție coruptă → default */ }

    let dragged = false;
    let startX = 0, startY = 0, origX = 0, origY = 0;
    pill.addEventListener('pointerdown', (ev: PointerEvent) => {
        dragged = false;
        startX = ev.clientX; startY = ev.clientY;
        const r = pill.getBoundingClientRect();
        origX = r.left; origY = r.top;
        pill.setPointerCapture(ev.pointerId);
    });
    pill.addEventListener('pointermove', (ev: PointerEvent) => {
        if (!pill.hasPointerCapture(ev.pointerId)) return;
        const dx = ev.clientX - startX, dy = ev.clientY - startY;
        if (!dragged && Math.hypot(dx, dy) < 8) return; // tap, nu drag
        dragged = true;
        const p = _lpClampPos(origX + dx, origY + dy, pill);
        pill.style.left = p.x + 'px';
        pill.style.top = p.y + 'px';
        pill.style.bottom = 'auto';
        pill.style.transform = 'none';
    });
    pill.addEventListener('pointerup', (ev: PointerEvent) => {
        pill.releasePointerCapture(ev.pointerId);
        if (dragged) {
            const r = pill.getBoundingClientRect();
            try { localStorage.setItem(_LP_PILL_POS_KEY, JSON.stringify({ x: r.left, y: r.top })); } catch (_e) { /* plin */ }
            return; // tragere = doar mutare, nu navigare
        }
        lpRemoveBackPill();
        onBack();
    });

    if (!(window as any)._lpNavHooked) {
        (window as any)._lpNavHooked = true;
        // navigare manuală din meniu → contextul de revenire nu mai e valabil
        document.querySelectorAll('.nav-item[data-section], .nav-links a[data-section]').forEach(a => {
            a.addEventListener('click', () => lpRemoveBackPill());
        });
    }
}

/* ── Ce e deschis pe linie (banda maro) ────────────────────────────────────
 * Dan, 20 aug 2026: „dacă apăs pe o linie care e în producție nu se deschide și
 * nici nu zice nimic. Eventual să se deschidă — dar doar în vizualizare — sau să
 * se poată face doar închidere, finalizare intervenție, ca să vedem dacă nu e
 * închisă vreo intervenție și de fapt ea e gata."
 *
 * Vechea navigare arunca omul în „Jurnal → Intervenții anterioare" prin trei
 * temporizatoare înlănțuite; dacă unul rata, ecranul nu se schimba și nimic nu
 * spunea de ce. Aici e o listă citită direct: ce e deschis, de când, pe ce utilaj,
 * cu O SINGURĂ acțiune — finalizarea. Fără editare, fără pași, fără confuzie.
 */
function _varstaText(dinIso: string | null | undefined): string {
    const t = Date.parse((dinIso || '').replace(' ', 'T')) || 0;
    if (!t) return 'fără dată';
    const zile = Math.floor((Date.now() - t) / 86400000);
    if (zile >= 1) return `${zile} ${zile === 1 ? 'zi' : 'zile'}`;
    const ore = Math.max(1, Math.round((Date.now() - t) / 3600000));
    return `${ore} h`;
}

async function _deschideUrgenteLinie(lineId: string, machineIds: string[]): Promise<void> {
    let toate: any[] = [];
    try {
        toate = await api('/api/work/maintenance_log/active') || [];
    } catch (e: any) {
        toast('Nu am putut citi intervențiile: ' + (e?.message || e), 'error');
        return;
    }
    const aleLiniei = toate.filter((r: any) => machineIds.includes(r.machine_id));
    const gazda = document.getElementById('lp-urgente-overlay') || (() => {
        const d = document.createElement('div');
        d.id = 'lp-urgente-overlay';
        d.className = 'lp-urg-overlay';
        document.body.appendChild(d);
        return d;
    })();

    const randuri = aleLiniei.length ? aleLiniei.map((r: any) => {
        const stare = r.status === 'paused' ? '⏸ în pauză' : '▶ în lucru';
        const ref = r.paused_at || r.start_time || r.date;
        const veche = (Date.now() - (Date.parse((ref || '').replace(' ', 'T')) || Date.now())) > 3 * 86400000;
        return `<div class="lp-urg-rand">
            <div class="lp-urg-cap">
                <strong>${escHtml(r.registration_number || ('#' + r.id))}</strong>
                <span class="lp-urg-masina">${escHtml(r.machine_name || r.machine_id || '')}</span>
                <span class="lp-urg-stare">${stare}</span>
                <span class="lp-urg-varsta${veche ? ' lp-urg-veche' : ''}">de ${_varstaText(ref)}</span>
            </div>
            <div class="lp-urg-text">${escHtml((r.action || '').slice(0, 160))}</div>
            <div class="lp-urg-jos">
                <span class="lp-urg-tehnician">${escHtml(r.technician || '—')}</span>
                <button type="button" class="lp-urg-vezi" data-log="${r.id}">👁 Vezi fișa</button>
                <button type="button" class="lp-urg-inchide" data-log="${r.id}"
                        data-eticheta="${escHtml(r.registration_number || ('#' + r.id))}">✓ Finalizează</button>
            </div>
        </div>`;
    }).join('') : '<div class="lp-urg-gol">Nicio intervenție deschisă pe această linie.</div>';

    gazda.innerHTML = `<div class="lp-urg-panou" role="dialog" aria-label="Intervenții deschise">
        <div class="lp-urg-antet">
            <div>
                <div class="lp-urg-titlu">Intervenții deschise — linia ${escHtml(lineId)}</div>
                <div class="lp-urg-sub">Doar vizualizare. Singura acțiune e finalizarea, pentru cele care sunt de fapt gata.</div>
            </div>
            <button type="button" class="lp-urg-x" aria-label="Închide">✕</button>
        </div>
        <div class="lp-urg-lista">${randuri}</div>
    </div>`;
    gazda.style.display = 'flex';

    const inchide = () => { gazda.style.display = 'none'; gazda.innerHTML = ''; };
    gazda.querySelector('.lp-urg-x')?.addEventListener('click', inchide);
    gazda.addEventListener('click', (ev) => { if (ev.target === gazda) inchide(); });

    gazda.querySelectorAll<HTMLElement>('.lp-urg-vezi').forEach(b => {
        b.onclick = () => {
            const w = window as any;
            if (typeof w.openLogDetail === 'function') { inchide(); w.openLogDetail(parseInt(b.dataset.log || '0', 10)); }
            else toast('Fișa se deschide din Jurnal → Intervenții anterioare', 'info');
        };
    });
    gazda.querySelectorAll<HTMLElement>('.lp-urg-inchide').forEach(b => {
        b.onclick = async () => {
            const eticheta = b.dataset.eticheta || '';
            const ok = await askConfirm(
                `Finalizezi ${eticheta}?`,
                'Intervenția se închide (din contul tău de inginer/master se validează pe numele tău). Nu se șterge nimic — rămâne în jurnal.',
                'Da, e gata');
            if (!ok) return;
            b.textContent = '⏳...';
            try {
                await api('/api/maintenance_log/' + b.dataset.log, {
                    method: 'PUT', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        status: 'completed',
                        completed_time: new Date().toISOString(),
                    }),
                });
                toast(`${eticheta} — finalizată`, 'success');
                inchide();
                (window as any).refreshLinePanels?.();
            } catch (e: any) {
                toast('Nu am putut finaliza: ' + (e?.message || e), 'error');
                b.textContent = '✓ Finalizează';
            }
        };
    });
}

/** Delegare de evenimente — o singură dată per container (fără onclick inline). */
export function wireLinePanelContainer(container: HTMLElement): void {
    if (container.dataset.lpWired === '1') return;
    container.dataset.lpWired = '1';
    container.addEventListener('click', (ev) => {
        const t = ev.target as HTMLElement;
        const w = window as any;
        const chip = t.closest('.lp-chip') as HTMLElement | null;
        if (chip && chip.dataset.machine) {
            ev.stopPropagation();
            // B3: panoul de utilaj nou; fallback pe detaliul vechi dacă lipsește.
            if (typeof w.openMachinePanel === 'function') w.openMachinePanel(chip.dataset.machine);
            else if (typeof w.openLineDetail === 'function') w.openLineDetail(chip.dataset.machine, chip.dataset.mname || '', state.currentUser?.role || 'operator');
            return;
        }
        const ment = t.closest('[data-ment-linie]') as HTMLElement | null;
        if (ment && ment.dataset.mentLinie) {
            ev.stopPropagation();
            const lid = ment.dataset.mentLinie;
            lpPushReturnAndGo(() => w.openAlarmHistoryForLine?.(lid));
            return;
        }
        const dese = t.closest('[data-dese-linie]') as HTMLElement | null;
        if (dese && dese.dataset.deseLinie) {
            ev.stopPropagation();
            const lid = dese.dataset.deseLinie;
            lpPushReturnAndGo(() => {
                w.openAlarmHistoryForLine?.(lid);
                // secțiunea „Prea dese” se încarcă odată cu istoricul; o aducem în vedere
                setTimeout(() => document.getElementById('ah-dese')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 1200);
            });
            return;
        }
        const atent = t.closest('[data-atentionari-linie]') as HTMLElement | null;
        if (atent && atent.dataset.atentionariLinie) {
            ev.stopPropagation();
            deschideAlarme('utilaje', atent.dataset.atentionariLinie);
            return;
        }
        const badge = t.closest('.lp-alarm-badge') as HTMLElement | null;
        if (badge && badge.dataset.lineAlarms) {
            ev.stopPropagation();
            const lid = badge.dataset.lineAlarms;
            lpPushReturnAndGo(() => w.openAlarmHistoryForLine?.(lid));
            return;
        }
        const btn = t.closest('[data-act]') as HTMLElement | null;
        if (!btn) {
            /* Clic pe TOT cardul de linie = „Detalii linie" (audit 20 sep 2026,
             * §3.10): pe tabletă omul apasă cardul, nu butonul mic — și nu se
             * întâmpla nimic. Chip-urile, clopoțelul și butoanele și-au păstrat
             * treaba mai sus; restul suprafeței duce la detalii. */
            if (t.closest('button, a, select, input, label')) return;
            const card = t.closest('.lp-card') as HTMLElement | null;
            if (!card || !card.dataset.line || card.dataset.line === '_utils') return;
            const w = window as any;
            if (card.classList.contains('dl-card')) { w.deschideLinieD?.(card.dataset.line); return; }
            if (typeof w.openLineLayoutDetail === 'function') w.openLineLayoutDetail(card.dataset.line, card.dataset.product || '');
            return;
        }
        ev.stopPropagation();
        const lid = btn.dataset.line || '';
        switch (btn.dataset.act) {
            case 'tasks':
                lpPushReturnAndGo(() => w._dashMyTasks?.(lid));
                break;
            case 'urmeaza':   // 1 nov 2026: „Ce urmează pe linii”, pe linia asta (ce-urmeaza.ts)
                lpPushReturnAndGo(() => w.deschideCeUrmeaza?.(lid));
                break;
            case 'alarms':
                lpPushReturnAndGo(() => w.openAlarmHistoryForLine?.(lid));
                break;
            case 'ai':
                lpPushReturnAndGo(() => w._dashAskAI?.(lid, btn.dataset.main || ''));
                break;
            case 'detail':
                if (typeof w.openLineLayoutDetail === 'function') w.openLineLayoutDetail(lid, btn.dataset.product || '');
                break;
            case 'linie-d':   // tema D: fereastra liniei (linie-d.ts)
                w.deschideLinieD?.(lid);
                break;
            case 'config':
                if (typeof w.openLineConfig === 'function') w.openLineConfig(lid);
                break;
            case 'clear-stop': {
                const mid = btn.dataset.machine || '';
                if (!mid) break;
                (async () => {
                    const ok = await askConfirm(
                        `Scoți marcajul de pe ${mid}?`,
                        'Utilajul revine la starea normală: alarmele calendaristice nu mai sunt ascunse. '
                        + 'Nu se șterge nicio lucrare — doar marcajul de „oprit pentru mentenanță".',
                        'Da, scoate-l');
                    if (!ok) return;
                    try {
                        await api(`/api/machines/${encodeURIComponent(mid)}/clear_maintenance_stop`,
                                  { method: 'POST' });
                        toast(`Marcajul de pe ${mid} a fost scos`, 'success');
                        (window as any).refreshLinePanels?.();
                    } catch (e: any) {
                        toast('Nu am putut scoate marcajul: ' + (e?.message || e), 'error');
                    }
                })();
                break;
            }
            case 'signal': {
                const kind = btn.dataset.kind || '';
                if (kind === 'urgent') {
                    // lista de pe loc, în loc de trei temporizatoare înlănțuite care
                    // uneori nu duceau nicăieri (raportat de Dan pe 20 aug)
                    const idsLinie = (btn.dataset.masini || '').split(',').filter(Boolean);
                    _deschideUrgenteLinie(lid, idsLinie);
                }
                else if (kind === 'overdue') lpPushReturnAndGo(() => w._dashMyTasks?.(lid));
                else lpPushReturnAndGo(() => w.openAlarmHistoryForLine?.(lid));
                break;
            }
        }
    });
}
